import axios from "axios";
import * as Sentry from "@sentry/react";
import { skipWhile, take } from "rxjs";
import { createAction } from "@reduxjs/toolkit";
import type { Organization, Project, Story } from "@theirstory/common/api_types/backend";

import { MAX_IMAGE_UPLOAD_BYTES } from "../../../components/imageUpload/constants";
import type { AsyncTaskStatus, AsyncTaskType } from "../inheritFromWeeb-ui-v2/generated/graphql";
import { getTheirStorySdk } from "../inheritFromWeeb-ui-v2/state/gqlClient";
import type { ActionTypeHelper, AppDispatch } from "../rootStore";
import {
  STORIES_LOAD_FAILED,
  STORIES_LOADED,
  STORIES_LOADING,
  STORIES_ONE_UPDATED,
  STORIES_SET_PAGE_VIEW,
  STORIES_CLEAR,
  STORY_TASK_UPDATED,
  STORIES_SORT_SETTING,
  STORIES_GALLERY_FILTERS_LOADED,
  STORIES_GALLERY_FILTER_LOADED,
  STORIES_TOGGLE_SELECTED_STORY,
  STORIES_SET_SELECTED_STORIES,
  STORIES_CLEAR_SELECTED_STORIES,
} from "../../actions/types";
import type { RootState } from "../rootReducer";
import { notifyError, notifySuccess } from "../../actions/notify";

import type { AsyncTaskId, StoryId } from "./reducer";

export const loadStoriesMyInterview =
  (filterProjectIds?: string[]) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const {
        stories: {
          paging: { size: pageSize, current: currentPage },
          sort: { column, order },
        },
      } = getState();

      // We put an skeleton loader when we are loading the first page, after that is handled by the video card list
      if (currentPage === 1) {
        dispatch({ type: STORIES_LOADING });
      }

      const urlPath = "/stories";
      const searchParam = window.location.search;
      const urlParams = new URLSearchParams(searchParam);

      const search = urlParams.get("search");
      const transcriptSearch = urlParams.get("transcriptSearch");

      let searchQuery = "";
      if (search) {
        searchQuery = `?search=${search}`;
      } else if (transcriptSearch) {
        searchQuery = `?search=${transcriptSearch}`;
      }

      const res = await axios.get(`${urlPath}/${searchQuery}`, {
        params: {
          pageSize,
          page: currentPage,
          sort: column,
          direction: order,
          ...(filterProjectIds && filterProjectIds.length > 0 ? { filterProjects: filterProjectIds.join(",") } : {}),
        },
      });

      const { items: newStories, total, allStoriesArrayTranscripts } = res.data;

      // Dedupe by _id when merging paginated pages so a story shifting across a
      // page boundary (or a stale cached page) can't render twice. Keeps first occurrence.
      const merged = [...getState().stories.stories, ...newStories];
      const seen = new Set<string>();
      const stories = merged.filter((s) => {
        const key = s?._id ?? "";
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      let page = currentPage;

      if (stories.length < total) {
        page = currentPage + 1;
      }

      dispatch({
        type: STORIES_LOADED,
        payload: { stories, total, page, allStoriesArrayTranscripts },
      });
      return stories;
    } catch (err) {
      dispatch({ type: STORIES_LOAD_FAILED });
      throw err;
    }
  };

export const loadStoriesGalleryPage = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const {
      stories: {
        paging: { size: pageSize, current: currentPage },
        filters,
        sort: { column, order },
      },
    } = getState();

    const { selectedProject, projects: currentProjects } = filters;

    // // We put an skeleton loader when we are loading the first page, after that is handled by the video card list
    if (currentPage === 1) {
      dispatch({ type: STORIES_LOADING });
    }

    const urlPath = "/stories";
    const searchParam = window.location.search;
    const urlParams = new URLSearchParams(searchParam);

    const search = urlParams.get("search");

    let searchQuery = "";
    if (search) {
      searchQuery = `?search=${search}`;
    }

    const res = await axios.get(`${urlPath}/${searchQuery}`, {
      params: {
        filterProjects: selectedProject?._id,
        pageSize,
        page: currentPage,
        filter: 1,
        sort: column,
        direction: order,
      },
    });

    const { items: newStories, total, allStoriesArrayTranscripts, organizations, project } = res.data;

    if (currentProjects.length === 0) {
      dispatch(setFilters(organizations, project));
    }

    let stories = [];
    if (currentPage === 1) {
      stories = [...newStories];
    } else {
      stories = [...getState().stories.stories, ...newStories];
    }

    let page = currentPage;

    if (stories.length < total) {
      page = currentPage + 1;
    }

    dispatch({
      type: STORIES_LOADED,
      payload: { stories, total, page, allStoriesArrayTranscripts },
    });
    return stories;
  } catch (err) {
    dispatch({ type: STORIES_LOAD_FAILED });
    throw err;
  }
};

interface LoadStoriesPortalPageFilters {
  projectIds?: string[];
  folderIds?: string[];
  search?: string;
  deedOfGift?: "all" | "yes" | "no";
}

export const loadStoriesPortalPage =
  ({
    projectIds = [],
    folderIds = [],
    search = "",
    deedOfGift = "all",
  }: LoadStoriesPortalPageFilters = {}): ActionTypeHelper<Promise<Story[]>> =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const {
        stories: {
          paging: { size: pageSize, current: currentPage },
          sort: { column, order },
        },
      } = getState();

      if (currentPage === 1) {
        dispatch({ type: STORIES_LOADING, payload: { clear: true } });
      }

      const searchQuery = search ? `?search=${encodeURIComponent(search)}` : "";

      const res = await axios.get(`/stories/${searchQuery}`, {
        params: {
          filterProjects: projectIds.join(","),
          filterFolders: folderIds.join(","),
          deedOfGift: deedOfGift === "all" ? undefined : deedOfGift,
          pageSize,
          page: currentPage,
          filter: 1,
          sort: column,
          direction: order,
        },
      });

      const { items: newStories, total, allStoriesArrayTranscripts, folders: folderOptions } = res.data;
      const stories = currentPage === 1 ? [...newStories] : [...getState().stories.stories, ...newStories];
      const page = stories.length < total ? currentPage + 1 : currentPage;

      dispatch({
        type: STORIES_LOADED,
        payload: { stories, total, page, allStoriesArrayTranscripts, folderOptions },
      });
      return stories;
    } catch (err) {
      dispatch({ type: STORIES_LOAD_FAILED });
      throw err;
    }
  };

export const loadStoryForPortalContent =
  (storyId: string): ActionTypeHelper<Promise<Story>> =>
  async (dispatch: AppDispatch) => {
    const { data: story } = await axios.get<Story>(`/stories/${storyId}`);
    dispatch({ type: STORIES_ONE_UPDATED, payload: story });
    return story;
  };

export const setFilters =
  (organizations: Organization[], projects: Project[]) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      stories: { filters },
    } = getState();

    const updatedFilters = { ...filters, projects: [{ name: "All projects", _id: "" }, ...projects], organizations };

    dispatch({ type: STORIES_GALLERY_FILTERS_LOADED, payload: { filters: updatedFilters } });
  };

export const setProjectFilter = (project: Project) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const {
    stories: { filters },
  } = getState();
  const updatedFilters = { ...filters, selectedProject: { label: project.name, _id: project._id } };
  dispatch({ type: STORIES_GALLERY_FILTER_LOADED, payload: { filters: updatedFilters } });
};

export const setStoriesPageView =
  ({ currentPage, pageSize }: any) =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: STORIES_SET_PAGE_VIEW, payload: { currentPage, pageSize } });
    dispatch(loadStoriesMyInterview());
    // 123
  };

export const setStoriesSort = (column, order) => async (dispatch: AppDispatch) => {
  dispatch({ type: STORIES_SORT_SETTING, payload: { column, order } });
};

export const togglePublishToGroup = (story, groupId) => async (dispatch: AppDispatch) => {
  const isAdd = !story.groups.some((g) => g.group === groupId);
  const params = isAdd ? { addGroupId: groupId } : { removeGroupId: groupId };
  const res = await axios.patch(`/stories/${story._id}/edit`, params);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
  return isAdd;
};

export const exportToPermanentOrg = (storyId) => async (dispatch: AppDispatch) => {
  await axios.post(`/user/permanentExport/${storyId}`);
  const res = await axios.get(`/stories/${storyId}`);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
};

export const transcribe = (storyId, languageCode, regenerate) => async (dispatch: AppDispatch) => {
  await axios.post(`/stories/${storyId}/transcribe`, { language_code: languageCode, regenerate: regenerate });
  const res = await axios.get(`/stories/${storyId}`);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
};

export const retryMuxTranscode = (storyId) => async (dispatch: AppDispatch) => {
  await axios.post(`/stories/${storyId}/transcode`);
  const res = await axios.get(`/stories/${storyId}`);
  dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
};

export const updateStoryThumbnail = (storyId: string, file: File) => async (dispatch: AppDispatch) => {
  try {
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      dispatch(notifyError(`The maximum image size is ${MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024)} MB.`));
      return null;
    }

    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(`/stories/${storyId}/thumbnail`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
    dispatch(notifySuccess("Poster image updated successfully."));
    return res.data;
  } catch (error) {
    console.error("Failed to update story thumbnail:", error);
    Sentry.withScope((scope) => {
      scope.setExtra("storyId", storyId);
      scope.setExtra("fileName", file.name);
      scope.setExtra("fileSize", file.size);
      scope.setExtra("fileType", file.type);
      scope.setTag("method", "updateStoryThumbnail");
      Sentry.captureException(error);
    });

    if (axios.isAxiosError(error) && typeof error.response?.data?.message === "string") {
      dispatch(notifyError(error.response.data.message));
    } else {
      dispatch(notifyError("Failed to update background image."));
    }

    return null;
  }
};

export const RETRANSCRIPTION_MODELS = [
  { value: "whisper", label: "Whisper" },
  { value: "speechmatics", label: "Speechmatics" },
] as const;

export type RetranscriptionModel = typeof RETRANSCRIPTION_MODELS[number]["value"];

export const transcribeModeAdvanced =
  (storyId, language, regenerate, deepgramTranscription, model: RetranscriptionModel = "whisper") =>
  async (dispatch: AppDispatch) => {
    let transcriptMethod = deepgramTranscription ? "deepgram_transcribe" : "advanced_transcribe";
    const body: { language: string; regenerate: boolean; service?: RetranscriptionModel } = { language, regenerate };
    if (regenerate) {
      transcriptMethod = model === "speechmatics" ? "deepgram_transcribe" : "advanced_transcribe";
      if (model === "speechmatics") body.service = "speechmatics";
      await axios.post(`/transcripts/${storyId}/saveversion`);
    }
    await axios.post(`/stories/${storyId}/${transcriptMethod}`, body);
    const res = await axios.get(`/stories/${storyId}`);
    dispatch({ type: STORIES_ONE_UPDATED, payload: res.data });
  };

export const downloadLocalVideos =
  (storyId: string): ActionTypeHelper<Promise<string>> =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const sdk = getTheirStorySdk(dispatch, getState);

    const res = await sdk.getStoryAsyncTaskCount({
      storyId,
      type: "TranscodeLocalVideo",
      status: "Pending",
    });
    if (res.story.asyncTaskCount) {
      return Promise.resolve("<Pending>");
    }
    const response = await axios.get(`/stories/${storyId}/download-all-local-videos`);
    const taskId = response.data.buildDownloadTaskId;

    return new Promise((resolve, reject) => {
      sdk
        .watchAsyncTask({ taskId })
        .pipe(
          skipWhile((x) => x.asyncTaskUpdated?.status === "Pending"),
          take(1)
        )
        .subscribe({
          next: (value) => {
            if (value.asyncTaskUpdated.__typename !== "BuildDownloadTask") {
              reject(new Error(`Unexpected task type: ${value.asyncTaskUpdated.__typename}`));
            } else if (!value.asyncTaskUpdated) {
              reject(new Error("Task failed"));
            } else if (value.asyncTaskUpdated.status === "Completed") {
              resolve(value.asyncTaskUpdated.downloadUrl);
            } else if (value.asyncTaskUpdated.status === "Failed") {
              reject(new Error(`Task failed: '${value.asyncTaskUpdated.errorMsg}'`));
            }
          },
          error: reject,
        });
    });
  };

export const setSelectStory = (story: Story) => async (dispatch: AppDispatch) => {
  dispatch({ type: STORIES_TOGGLE_SELECTED_STORY, payload: story });
};

export const setSelectedStories = (stories: Story[]) => async (dispatch: AppDispatch) => {
  dispatch({ type: STORIES_SET_SELECTED_STORIES, payload: stories });
};

export const clearSelectedStories = () => async (dispatch: AppDispatch) => {
  dispatch({ type: STORIES_CLEAR_SELECTED_STORIES });
};

export interface StoryTaskUpdated {
  id: AsyncTaskId;
  storyId: StoryId;
  taskType: AsyncTaskType;
  status: AsyncTaskStatus;
}

export const storyTaskUpdated = createAction<StoryTaskUpdated>(STORY_TASK_UPDATED);

export const clearStories = createAction(STORIES_CLEAR);
